// StageCompletionQuiz - Quiz modal shown when completing a stage
import { useState, useEffect, useRef, useMemo } from 'react';
import { Volume2 } from 'lucide-react';
import { NPC_DATA } from '../../data/npcData';
import { useTTS } from '../../hooks/useTTS';

const StageCompletionQuiz = ({ stage, onComplete, onClose, voiceEnabled = true, playerVoice = 'alice' }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying } = useTTS();
  
  const quizQuestions = useMemo(() => {
    const questions = [];
    Object.entries(NPC_DATA).forEach(([npcId, npcData]) => {
      if (npcData.facts && npcData.facts[stage]) {
        questions.push({
          npcId,
          npcName: npcData.name,
          ...npcData.facts[stage]
        });
      }
    });
    const shuffled = questions.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(5, shuffled.length));
  }, [stage]);
  
  useEffect(() => {
    setTotalQuestions(quizQuestions.length);
    if (inputRef.current) inputRef.current.focus();
  }, [quizQuestions]);
  
  const currentQ = quizQuestions[currentQuestionIndex];
  
  // Read the question aloud when it changes
  useEffect(() => {
    if (currentQ && voiceEnabled && !showResult) {
      setTimeout(() => {
        speakAsPlayer(`About ${currentQ.npcName}: ${currentQ.question}`, playerVoice);
      }, 400);
    }
    return () => stopTTS();
  }, [currentQuestionIndex, showResult]);
  
  // Read the result aloud
  useEffect(() => {
    if (showResult && voiceEnabled) {
      const msg = isCorrect ? 'Correct! Well done!' : `Not quite. The answer was: ${currentQ?.answer}`;
      setTimeout(() => speakAsPlayer(msg, playerVoice), 300);
    }
  }, [showResult]);
  
  const readQuestion = () => {
    if (currentQ) {
      speakAsPlayer(`About ${currentQ.npcName}: ${currentQ.question}`, playerVoice);
    }
  };
  
  const checkAnswer = () => {
    if (!currentQ) return;
    const userAnswer = typedAnswer.toLowerCase().trim();
    const correctAnswer = currentQ.answer.toLowerCase().trim();
    const correct = userAnswer.includes(correctAnswer) || correctAnswer.includes(userAnswer);
    setIsCorrect(correct);
    if (correct) setScore(prev => prev + 1);
    setShowResult(true);
  };
  
  const nextQuestion = () => {
    stopTTS();
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setTypedAnswer('');
      setShowHint(false);
      setShowResult(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      onComplete(score + (isCorrect ? 1 : 0), totalQuestions);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && typedAnswer.trim()) {
      if (showResult) {
        nextQuestion();
      } else {
        checkAnswer();
      }
    }
    if (e.key === 'Escape') {
      stopTTS();
      onClose();
    }
  };
  
  if (!currentQ) return null;
  
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-lavender/95 via-sky-100/95 to-mint-green/95 backdrop-blur-sm z-[500] flex items-center justify-center p-4" data-testid="stage-quiz-modal">
      <div className="bg-white border-4 border-lavender rounded-3xl p-8 max-w-2xl w-full shadow-2xl shadow-lavender/30">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-lavender to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-3xl font-bold text-white">Q</span>
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-text-dark">Stage {stage} Quiz!</h2>
              <p className="text-text-medium">Test what you learned from the characters</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {voiceEnabled && (
              <button
                onClick={readQuestion}
                disabled={isPlaying}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isPlaying ? 'bg-mint-green/20 text-mint-green animate-pulse' : 'bg-lavender/20 text-lavender hover:bg-lavender/30'
                }`}
                data-testid="quiz-listen-btn"
                title="Listen to question"
              >
                <Volume2 className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={() => { stopTTS(); onClose(); }} 
              className="w-10 h-10 bg-coral-orange/20 hover:bg-coral-orange/40 rounded-full flex items-center justify-center text-coral-orange text-xl font-bold transition-all"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-text-medium mb-2">
            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
            <span>Score: {score}/{currentQuestionIndex + (showResult ? 1 : 0)}</span>
          </div>
          <div className="h-3 bg-soft-gray rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-lavender to-mint-green transition-all rounded-full"
              style={{ width: `${((currentQuestionIndex + (showResult ? 1 : 0)) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Question about NPC */}
        <div className="bg-gradient-to-r from-cream to-warm-white rounded-2xl p-6 mb-6 border-2 border-lavender/30">
          <p className="text-lavender text-sm font-semibold mb-2">About {currentQ.npcName}:</p>
          <p className="text-xl text-text-dark font-semibold">{currentQ.question}</p>
        </div>
        
        {/* Answer input */}
        {!showResult ? (
          <>
            <input
              ref={inputRef}
              type="text"
              value={typedAnswer}
              onChange={(e) => setTypedAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your answer..."
              className="w-full px-6 py-4 bg-white border-3 border-lavender/50 rounded-2xl text-text-dark text-xl focus:outline-none focus:border-lavender focus:ring-4 focus:ring-lavender/20 mb-4 shadow-sm"
              autoFocus
              autoComplete="off"
              data-testid="quiz-answer-input"
            />
            
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => setShowHint(true)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  showHint 
                    ? 'bg-sunny-yellow/20 text-sunny-yellow border-2 border-sunny-yellow' 
                    : 'bg-soft-gray text-text-medium hover:bg-sunny-yellow/20 hover:text-sunny-yellow'
                }`}
              >
                Need a hint?
              </button>
              {showHint && (
                <div className="flex-1 px-4 py-2 bg-sunny-yellow/20 border-2 border-sunny-yellow/50 rounded-xl text-sunny-yellow">
                  {currentQ.hint}
                </div>
              )}
            </div>
            
            <button
              onClick={checkAnswer}
              disabled={!typedAnswer.trim()}
              className={`w-full py-4 rounded-2xl font-bold text-xl transition-all ${
                typedAnswer.trim()
                  ? 'bg-gradient-to-r from-lavender to-purple-500 hover:from-purple-500 hover:to-lavender text-white shadow-lg'
                  : 'bg-soft-gray text-text-medium cursor-not-allowed'
              }`}
            >
              Check Answer
            </button>
          </>
        ) : (
          <div className="animate-fadeIn">
            <div className={`p-6 rounded-2xl mb-6 text-center ${
              isCorrect 
                ? 'bg-mint-green/20 border-2 border-mint-green' 
                : 'bg-coral-orange/20 border-2 border-coral-orange'
            }`}>
              <p className={`text-2xl font-display font-bold ${isCorrect ? 'text-mint-green' : 'text-coral-orange'}`}>
                {isCorrect ? 'Correct!' : 'Not quite!'}
              </p>
              {!isCorrect && (
                <p className="text-text-medium mt-2">
                  The answer was: <span className="font-bold text-text-dark">{currentQ.answer}</span>
                </p>
              )}
            </div>
            
            <button
              onClick={nextQuestion}
              className="w-full py-4 bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green rounded-2xl text-white font-bold text-xl transition-all shadow-lg"
            >
              {currentQuestionIndex < quizQuestions.length - 1 ? 'Next Question' : 'Finish Quiz'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StageCompletionQuiz;
